import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { X, Check, XCircle, Clock, Ban, Radio, Users } from 'lucide-react';
import { api } from '../../utils/api';

/**
 * Ride Match history for one trip — who was offered it and what they did.
 *
 * Answers what the trip document alone cannot: how many drivers saw it, which
 * one took it, who declined, and who never answered. Works for any status,
 * including a trip still being matched (it polls while the loop is live).
 *
 * The server merges the acting engine's `rideOffers` with the normal engine's
 * `rideMatchLog`, so this renders one shape regardless of which dispatcher owned
 * the trip.
 */

const STATE_STYLE = {
  ACCEPTED: { cls: 'bg-green-100 text-green-700', Icon: Check },
  REJECTED: { cls: 'bg-red-100 text-red-700', Icon: XCircle },
  TIMEOUT: { cls: 'bg-amber-100 text-amber-700', Icon: Clock },
  VOIDED: { cls: 'bg-gray-100 text-gray-600', Icon: Ban },
  SENT: { cls: 'bg-blue-100 text-blue-700', Icon: Radio },
};

const Tile = ({ label, value, tone }) => (
  <div className="bg-gray-50 rounded-lg p-3 text-center">
    <div className={`text-xl font-semibold tabular-nums ${tone || ''}`}>{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-0.5">{label}</div>
  </div>
);

const time = (ms) => (ms ? new Date(ms).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—');

const RideMatchModal = ({ tripId, rideId, onClose }) => {
  const { data, isLoading, error } = useQuery(
    ['trip-ridematch', tripId],
    () => api.get(`/trip-ridematch/${tripId}`).then((r) => r.data),
    {
      enabled: !!tripId,
      refetchOnWindowFocus: false,
      // Keep polling only while an offer is still open — a settled trip's
      // history never changes, so polling it would be pure noise.
      refetchInterval: (d) => (d?.data?.summary?.stillOpen > 0 ? 2000 : false),
    },
  );

  const d = data?.data;
  const s = d?.summary;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Users className="h-4 w-4" /> Ride Match — {rideId || d?.trip?.rideId || tripId}
            </h2>
            {d && (
              <p className="text-xs text-gray-500 mt-0.5">
                {d.trip.status}
                {d.trip.isActingDriverTrip ? ' · acting driver' : ' · normal ride'}
                {d.trip.failureReason ? ` · ${d.trip.failureReason}` : ''}
                {` · source: ${d.source}`}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 overflow-auto">
          {isLoading && <div className="text-sm text-gray-500 py-8 text-center">Loading…</div>}

          {error && (
            <div className="text-sm text-red-600 py-8 text-center">
              {error?.response?.data?.message || 'Could not load ride match history.'}
            </div>
          )}

          {d && (
            <>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-5">
                <Tile label="Drivers offered" value={s.driversOffered} />
                <Tile label="Total offers" value={s.totalOffers} />
                <Tile label="Accepted" value={s.accepted} tone="text-green-600" />
                <Tile label="Declined" value={s.declined} tone="text-red-600" />
                <Tile label="No response" value={s.noResponse} tone="text-amber-600" />
                <Tile label="Rounds" value={s.rounds} />
              </div>

              {s.stillOpen > 0 && (
                <div className="mb-4 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  An offer is still open — this updates live.
                </div>
              )}

              {d.offers.length === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center">
                  No driver was ever offered this trip.
                  {d.trip.matchFailedAt
                    ? ' Matching ran and found nobody available.'
                    : ' Matching may not have started yet.'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left">Driver</th>
                        <th className="px-3 py-2 text-left">Round</th>
                        <th className="px-3 py-2 text-left">Outcome</th>
                        <th className="px-3 py-2 text-left">Replied in</th>
                        <th className="px-3 py-2 text-left">Score / dist</th>
                        <th className="px-3 py-2 text-left">Offered at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.offers.map((o, i) => {
                        const st = STATE_STYLE[o.state] || STATE_STYLE.VOIDED;
                        const { Icon } = st;
                        const isWinner = o.state === 'ACCEPTED';
                        return (
                          <tr
                            key={`${o.driverId}-${o.offerId || i}`}
                            className={`border-b border-gray-100 ${isWinner ? 'bg-green-50' : ''}`}
                          >
                            <td className="px-3 py-2 text-gray-500 tabular-nums">{i + 1}</td>
                            <td className="px-3 py-2">
                              <div className="font-medium">{o.driverName || '(unnamed)'}</div>
                              <div className="font-mono text-[11px] text-gray-500">{o.driverId}</div>
                              {o.driverPhone && (
                                <div className="text-[11px] text-gray-400">{o.driverPhone}</div>
                              )}
                            </td>
                            <td className="px-3 py-2 tabular-nums">{o.round}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${st.cls}`}>
                                <Icon className="h-3 w-3" /> {o.outcome}
                              </span>
                            </td>
                            <td className="px-3 py-2 tabular-nums text-gray-600">
                              {o.responseSeconds != null ? `${o.responseSeconds}s` : '—'}
                            </td>
                            <td className="px-3 py-2 tabular-nums text-gray-600">
                              {o.score != null ? o.score : '—'}
                              {o.distanceKm != null && (
                                <span className="text-gray-400"> · {o.distanceKm} km</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-500 text-[11px]">{time(o.sentAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Acting engine only: drivers passed over before any offer. */}
              {d.skipped?.length > 0 && (
                <div className="mt-5">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    Skipped — not offered ({d.skipped.length})
                  </h3>
                  <ul className="text-sm border border-gray-100 rounded-lg divide-y divide-gray-100">
                    {d.skipped.map((k, i) => (
                      <li key={`${k.driverId}-${i}`} className="px-3 py-2 flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium">{k.driverName || '(unnamed)'}</div>
                          <div className="font-mono text-[11px] text-gray-500">{k.driverId}</div>
                        </div>
                        <div className="text-xs text-gray-600 text-right">
                          {k.reason}
                          <div className="text-[11px] text-gray-400">{time(k.at)}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Acting engine only: the trip-level story, wave by wave. */}
              {d.timeline?.length > 0 && (
                <div className="mt-5">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    Dispatch timeline
                  </h3>
                  <ol className="text-sm border-l-2 border-gray-200 ml-1.5 space-y-2">
                    {d.timeline.map((ev, i) => (
                      <li key={`${ev.event}-${i}`} className="pl-3 relative">
                        <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-gray-400" />
                        <span className="text-gray-800">
                          {ev.text}
                          {ev.driverName ? ` — ${ev.driverName}` : ''}
                        </span>
                        <span className="text-[11px] text-gray-400 ml-2">{time(ev.at)}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <p className="text-[11px] text-gray-400 mt-4">
                {d.trip.isActingDriverTrip
                  ? 'Each round is one wave: the trip is offered to several drivers at once, the first to accept gets it, and the offer is withdrawn from the rest.'
                  : 'A driver can appear more than once: exclusions are cleared between retry rounds, so someone who declined in round 1 is asked again in round 2.'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

RideMatchModal.propTypes = {
  tripId: PropTypes.string.isRequired,
  rideId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default RideMatchModal;
